import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PatternEditor = () => {
  const { patternId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const isEdit = !!patternId;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    breaths_count: 30,
    inhale_duration: 2.0,
    exhale_duration: 1.5,
    apnea_duration: null,
    rounds: 3
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchPattern();
    }
  }, [patternId]);

  const fetchPattern = async () => {
    try {
      const response = await axios.get(`${API}/patterns`, {
        headers: getAuthHeaders()
      });
      const pattern = response.data.find(p => p.id === patternId);
      if (pattern) {
        if (pattern.is_predefined) {
          toast.error('No puedes editar patrones predefinidos');
          navigate('/');
          return;
        }
        setFormData({
          name: pattern.name,
          description: pattern.description || '',
          breaths_count: pattern.breaths_count,
          inhale_duration: pattern.inhale_duration,
          exhale_duration: pattern.exhale_duration,
          apnea_duration: pattern.apnea_duration,
          rounds: pattern.rounds
        });
      } else {
        toast.error('Patrón no encontrado');
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching pattern:', error);
      toast.error('Error al cargar patrón');
      navigate('/');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ['breaths_count', 'rounds'].includes(name) 
        ? parseInt(value) || 0
        : ['inhale_duration', 'exhale_duration', 'apnea_duration'].includes(name)
        ? parseFloat(value) || 0
        : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        apnea_duration: formData.apnea_duration || null
      };

      if (isEdit) {
        await axios.put(`${API}/patterns/${patternId}`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Patrón actualizado exitosamente');
      } else {
        await axios.post(`${API}/patterns`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Patrón creado exitosamente');
      }
      navigate('/');
    } catch (error) {
      console.error('Error saving pattern:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar patrón');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este patrón?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/patterns/${patternId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Patrón eliminado exitosamente');
      navigate('/');
    } catch (error) {
      console.error('Error deleting pattern:', error);
      toast.error('Error al eliminar patrón');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl" data-testid="pattern-editor">
      <div className="mb-6">
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="rounded-full"
          data-testid="back-button"
        >
          <ArrowLeft size={18} className="mr-2" />
          Volver
        </Button>
      </div>

      <div className="glass rounded-2xl p-8">
        <h1 className="text-3xl font-bold mb-6">
          {isEdit ? 'Editar Patrón' : 'Crear Patrón Personalizado'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="name">Nombre del Patrón *</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="mt-1"
              placeholder="Mi patrón personalizado"
              data-testid="pattern-name-input"
            />
          </div>

          <div>
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="mt-1"
              placeholder="Describe tu patrón de respiración..."
              rows={3}
              data-testid="pattern-description-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="breaths_count">Número de Respiraciones *</Label>
              <Input
                id="breaths_count"
                name="breaths_count"
                type="number"
                min="1"
                max="100"
                value={formData.breaths_count}
                onChange={handleChange}
                required
                className="mt-1"
                data-testid="breaths-count-input"
              />
            </div>

            <div>
              <Label htmlFor="rounds">Número de Rondas *</Label>
              <Input
                id="rounds"
                name="rounds"
                type="number"
                min="1"
                max="10"
                value={formData.rounds}
                onChange={handleChange}
                required
                className="mt-1"
                data-testid="rounds-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="inhale_duration">Duración Inhalación (segundos) *</Label>
              <Input
                id="inhale_duration"
                name="inhale_duration"
                type="number"
                step="0.1"
                min="0.5"
                max="10"
                value={formData.inhale_duration}
                onChange={handleChange}
                required
                className="mt-1"
                data-testid="inhale-duration-input"
              />
            </div>

            <div>
              <Label htmlFor="exhale_duration">Duración Exhalación (segundos) *</Label>
              <Input
                id="exhale_duration"
                name="exhale_duration"
                type="number"
                step="0.1"
                min="0.5"
                max="10"
                value={formData.exhale_duration}
                onChange={handleChange}
                required
                className="mt-1"
                data-testid="exhale-duration-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="apnea_duration">Duración Apnea (segundos - opcional)</Label>
            <Input
              id="apnea_duration"
              name="apnea_duration"
              type="number"
              step="0.1"
              min="0"
              value={formData.apnea_duration || ''}
              onChange={handleChange}
              className="mt-1"
              placeholder="Dejar vacío para apnea libre"
              data-testid="apnea-duration-input"
            />
            <p className="text-sm text-gray-600 mt-1">
              Si dejas este campo vacío, la apnea será controlada manualmente
            </p>
          </div>

          <div className="flex gap-4">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#77C9D4] hover:bg-[#5AB4C1] !text-white dark:!text-black rounded-full py-6 text-lg"
              data-testid="save-pattern-button"
            >
              <Save size={20} className="mr-2" />
              {loading ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear Patrón'}
            </Button>

            {isEdit && (
              <Button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                variant="destructive"
                className="rounded-full px-6 py-6"
                data-testid="delete-pattern-button"
              >
                <Trash2 size={20} />
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default PatternEditor;